import React, { useState, useEffect } from 'react';
import { Button, Input, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { vehiclesAPI } from '../../helpers/api';

interface Vehicle {
  id: string;
  plate: string;
  brand: string | null;
  model: string | null;
  type: string;
  status: string;
}

interface SiteVehicle {
  id: string;
  vehicle: Vehicle;
  assignedAt: string;
}

interface ProjectVehicle {
  id: string;
  vehicle: Vehicle;
}

interface Site {
  id: string;
  name: string;
  code: string;
  project: { id?: string; name: string; city: { name: string } };
}

interface SiteVehiclesModalProps {
  isOpen: boolean;
  toggle: () => void;
  site: Site | null;
  projectId: string | null;
  onUpdate?: () => void;
}

const typeLabels: Record<string, string> = {
  COMPANY: 'Şirket Aracı',
  RENTAL: 'Kiralık',
  MACHINERY: 'İş Makinesi',
};

const typeIcons: Record<string, string> = {
  COMPANY: 'ri-truck-line',
  RENTAL: 'ri-car-washing-line',
  MACHINERY: 'ri-steering-2-line',
};

const typeColors: Record<string, string> = {
  COMPANY: 'text-primary',
  RENTAL: 'text-info',
  MACHINERY: 'text-warning',
};

const statusStyles: Record<string, string> = {
  ACTIVE: 'bg-success bg-opacity-10 text-success',
  MAINTENANCE: 'bg-warning bg-opacity-10 text-warning',
  INACTIVE: 'bg-secondary bg-opacity-10 text-secondary',
};

const statusLabels: Record<string, string> = {
  ACTIVE: 'Aktif',
  MAINTENANCE: 'Bakımda',
  INACTIVE: 'Pasif',
};

const SiteVehiclesModal: React.FC<SiteVehiclesModalProps> = ({
  isOpen,
  toggle,
  site,
  projectId,
  onUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [siteVehicles, setSiteVehicles] = useState<SiteVehicle[]>([]);
  const [unassignedVehicles, setUnassignedVehicles] = useState<ProjectVehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [adding, setAdding] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && site) {
      fetchData();
    }
  }, [isOpen, site]);

  const fetchData = async () => {
    if (!site) return;

    try {
      setLoading(true);
      const [siteRes, unassignedRes] = await Promise.all([
        vehiclesAPI.getSiteVehicles(site.id),
        vehiclesAPI.getUnassignedForSite(site.id),
      ]);
      setSiteVehicles(siteRes.data);
      setUnassignedVehicles(unassignedRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Araçlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (vehicleId: string) => {
    if (!site) return;

    try {
      setAdding(vehicleId);
      await vehiclesAPI.assignToSite(vehicleId, site.id);
      toast.success('Araç şantiyeye atandı');
      fetchData();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Atama başarısız');
    } finally {
      setAdding(null);
    }
  };

  const handleRemove = async (vehicleId: string) => {
    if (!site) return;
    if (!window.confirm('Aracı şantiyeden çıkarmak istediğinize emin misiniz?')) return;

    try {
      setRemoving(vehicleId);
      await vehiclesAPI.removeFromSite(vehicleId, site.id);
      toast.success('Araç şantiyeden çıkarıldı');
      fetchData();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setRemoving(null);
    }
  };

  const filteredUnassigned = unassignedVehicles.filter(
    (pv) =>
      pv.vehicle.plate.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pv.vehicle.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pv.vehicle.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      typeLabels[pv.vehicle.type]?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleExpand = () => setIsExpanded(!isExpanded);

  if (!site) return null;

  const headerActions = (
    <>
      <button
        type="button"
        className="side-modal-expand-btn me-1"
        onClick={() => setShowAddPanel(!showAddPanel)}
        title={showAddPanel ? 'Listeye Dön' : 'Araç Ekle'}
        style={{ color: showAddPanel ? '#f06548' : '#0ab39c' }}
      >
        <i className={showAddPanel ? 'ri-arrow-left-line' : 'ri-add-line'}></i>
      </button>
      <button
        type="button"
        className="side-modal-expand-btn"
        onClick={toggleExpand}
        title={isExpanded ? 'Küçült' : 'Genişlet'}
      >
        <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
      </button>
    </>
  );

  // Group by type for summary
  const vehiclesByType = siteVehicles.reduce((acc, sv) => {
    const type = sv.vehicle.type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(sv.vehicle);
    return acc;
  }, {} as Record<string, Vehicle[]>);

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="Şantiye Araçları"
      size={isExpanded ? 'expanded' : 'lg'}
      headerActions={headerActions}
      hideBackdrop
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {/* Header Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }} className="rounded-3 p-3 mb-3">
          <h4 className="mb-1" style={{ fontWeight: 600 }}>
            {site.name}
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            {site.project.city.name} • {site.project.name}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        ) : showAddPanel ? (
          /* Add Vehicle Panel */
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f06548' }} className="rounded-3 p-3 mb-3">
            <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Şantiyeye Araç Ekle
            </div>
            <Input
              type="text"
              placeholder="Plaka, marka veya tip ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mb-3"
            />
            {filteredUnassigned.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted mb-0">
                  {searchTerm
                    ? 'Aramayla eşleşen araç bulunamadı'
                    : 'Projede atanabilecek araç bulunmuyor'}
                </p>
                <small className="text-muted d-block mt-2">
                  <i className="ri-information-line me-1"></i>
                  Araçların önce projeye atanması gerekir
                </small>
              </div>
            ) : (
              <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
                {filteredUnassigned.map((pv, idx) => (
                  <div
                    key={pv.id}
                    className="d-flex align-items-center justify-content-between px-3 py-2"
                    style={{ borderBottom: idx < filteredUnassigned.length - 1 ? '1px solid #e9ecef' : 'none' }}
                  >
                    <div>
                      <div className="d-flex align-items-center">
                        <i className={`${typeIcons[pv.vehicle.type] || 'ri-car-line'} me-2 ${typeColors[pv.vehicle.type] || 'text-primary'}`}></i>
                        <span style={{ fontWeight: 500 }}>{pv.vehicle.plate}</span>
                        <span
                          className={`ms-2 px-2 py-0 rounded-pill small ${statusStyles[pv.vehicle.status]}`}
                          style={{ fontSize: '0.7rem' }}
                        >
                          {statusLabels[pv.vehicle.status]}
                        </span>
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.85rem', marginLeft: '1.5rem' }}>
                        {pv.vehicle.brand} {pv.vehicle.model} • {typeLabels[pv.vehicle.type] || pv.vehicle.type}
                      </div>
                    </div>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() => handleAssign(pv.vehicle.id)}
                      disabled={adding === pv.vehicle.id}
                    >
                      {adding === pv.vehicle.id ? <Spinner size="sm" /> : <i className="ri-add-line"></i>}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Summary */}
            {siteVehicles.length > 0 && (
              <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3 mb-3">
                <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Özet
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {Object.entries(vehiclesByType).map(([type, vList]) => (
                    <div
                      key={type}
                      className="px-3 py-2 rounded-3"
                      style={{ fontSize: '0.85rem', background: '#f8f9fb', border: '1px solid #f2f4f7' }}
                    >
                      <i className={`${typeIcons[type] || 'ri-car-line'} ${typeColors[type] || 'text-primary'} me-1`}></i>
                      <span style={{ fontWeight: 500 }}>{vList.length}</span>
                      <span className="ms-1">{typeLabels[type] || type}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Vehicles List */}
            <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f06548' }} className="rounded-3 p-3 mb-3">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Şantiye Araçları
                </span>
                <span className="text-muted small">{siteVehicles.length} araç</span>
              </div>
              {siteVehicles.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted mb-3">Bu şantiyeye henüz araç atanmamış</p>
                  <Button color="primary" size="sm" onClick={() => setShowAddPanel(true)}>
                    <i className="ri-add-line me-1"></i>
                    Araç Ekle
                  </Button>
                </div>
              ) : (
                <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
                  {siteVehicles.map((sv, idx) => (
                    <div
                      key={sv.id}
                      className="px-3 py-3"
                      style={{ borderBottom: idx < siteVehicles.length - 1 ? '1px solid #e9ecef' : 'none' }}
                    >
                      <div className="d-flex align-items-start justify-content-between">
                        <div style={{ flex: 1 }}>
                          <div className="d-flex align-items-center mb-1">
                            <i className={`${typeIcons[sv.vehicle.type] || 'ri-car-line'} me-2 ${typeColors[sv.vehicle.type] || 'text-primary'}`}></i>
                            <span style={{ fontWeight: 500 }}>{sv.vehicle.plate}</span>
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.85rem', marginLeft: '1.5rem' }}>
                            {sv.vehicle.brand && (
                              <span className="me-2">
                                {sv.vehicle.brand} {sv.vehicle.model}
                              </span>
                            )}
                            <span>• {typeLabels[sv.vehicle.type] || sv.vehicle.type}</span>
                          </div>
                        </div>
                        <div className="d-flex align-items-center">
                          <span
                            className={`px-2 py-1 rounded-pill small me-2 ${statusStyles[sv.vehicle.status]}`}
                            style={{ fontWeight: 500, fontSize: '0.75rem' }}
                          >
                            {statusLabels[sv.vehicle.status]}
                          </span>
                          <Button
                            color="danger"
                            size="sm"
                            outline
                            onClick={() => handleRemove(sv.vehicle.id)}
                            disabled={removing === sv.vehicle.id}
                            style={{ padding: '0.15rem 0.4rem' }}
                          >
                            {removing === sv.vehicle.id ? (
                              <Spinner size="sm" />
                            ) : (
                              <i className="ri-delete-bin-line" style={{ fontSize: '0.8rem' }}></i>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info Note */}
            <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #299cdb' }} className="rounded-3 p-3 mb-3">
              <div className="d-flex align-items-start">
                <i className="ri-information-line text-info me-2" style={{ fontSize: '1.1rem' }}></i>
                <small className="text-muted">
                  Araçlar önce projeye atanır, sonra projedeki araçlar şantiyelere dağıtılabilir.
                </small>
              </div>
            </div>
          </>
        )}
      </div>
    </SideModal>
  );
};

export default SiteVehiclesModal;
